/* eslint-disable import/prefer-default-export */
import axios from '../axios';

// ---------------------------------------------------------------------------
// Routing — Service Layer
//
// Camada fina sobre o axios singleton. Faz parsing do envelope JSend e renomeia
// campos em snake_case → camelCase apenas nas FRONTEIRAS (paginação, raiz do
// envelope). Campos das linhas (order_number, customer_name, etc.) são mantidos
// em snake_case para preservar simetria com a Tela 2.
//
// Tratamento de erros NÃO acontece aqui — é responsabilidade das páginas
// (toast, fieldErrors). Ver seção 5.1 do handoff de arquitetura.
// ---------------------------------------------------------------------------

// Cada linha de scheduleItems traz `commission` (handoff 0140):
//   { entries_count, total }  → usuário com permissão de ler comissão
//   null                      → sem permissão (a coluna não deve aparecer)
// "n/a" se decide por entries_count === 0, nunca por total === 0 (há
// lançamento zerado, que é R$ 0,00).

// ===========================================================================
// LISTAGEM E DETALHE (Tela 1 e Tela 2)
// ===========================================================================

export async function searchScheduleItems(filters) {
   // Traduz chaves camelCase (vindas do componente) → snake_case (esperadas
   // pelo backend no body). Sem isso, o backend ignora todos os filtros
   // silenciosamente e devolve todos os items.
   // Endpoint migrado para POST /routing/items/search (padrão BillingController.search).
   const body = {
      date_from:       filters?.dateFrom,
      date_to:         filters?.dateTo,
      schedule_period: filters?.schedulePeriod,
      item_status:     filters?.itemStatus,
      technician_id:   filters?.technicianId,
      department_id:   filters?.departmentId,
      // Ponto 4: filtro de base opcional (código GOIANIA/ANAPOLIS). Ausente →
      // todas as bases; código inválido → 404 no backend.
      cluster_origin:  filters?.clusterOrigin,
      search_term:     filters?.searchTerm,
      // Handoff 0140: true = tem ≥ 1 lançamento de comissão (inclusive R$ 0,00);
      // false = nenhum lançamento. Ausente → todos. Só pode ir para quem tem
      // routing:commission_entry:create OU routing:finance:operate — para os
      // demais o backend responde 403 commission_read_forbidden (não ignora).
      has_commission:  filters?.hasCommission,
      page:            filters?.page,
      limit:           filters?.limit,
      sort_by:         filters?.sortBy,
      sort_direction:  filters?.sortDirection,
   };

   const response = await axios.post('/routing/items/search', body);
   const envelope = response?.data ?? {};
   const data = envelope.data ?? {};
   const meta = envelope.meta ?? {};

   return {
      scheduleItems: data.scheduleItems ?? [],
      total: meta.total ?? 0,
      // Soma dos order_value dos pedidos que casam com o filtro (não só
      // os da página atual). Backend agrega antes de paginar e devolve
      // em data.summary.total_order_value.
      totalValue: data.summary?.total_order_value ?? 0,
      page: meta.page ?? 1,
      perPage: meta.per_page ?? 20,
      totalPages: meta.total_pages ?? 1,
   };
}

export async function getScheduleItemById(itemId) {
   const response = await axios.get(`/routing/items/${itemId}`);
   return response?.data?.data?.scheduleItem ?? null;
}

export async function updateScheduleItem(itemId, payload) {
   const response = await axios.put(`/routing/items/${itemId}`, payload);
   return {
      scheduleItem: response?.data?.data?.scheduleItem ?? null,
      message: response?.data?.message ?? 'Item atualizado.',
   };
}

export async function updateScheduleItemStatus(itemId, payload) {
   const response = await axios.patch(
      `/routing/items/${itemId}/status`,
      payload,
   );
   return {
      scheduleItem: response?.data?.data?.scheduleItem ?? null,
      message: response?.data?.message ?? 'Status atualizado.',
   };
}

// Cancela (soft delete) o item da rota. Backend exige item em PENDENTE;
// retorna 422 fail caso contrário. Permissão: routing:schedule_item:cancel
// OU routing:finance:operate.
//
// Handoff 0048 (breaking): o DELETE passou a EXIGIR cancellation_reason_id no
// body (rastreabilidade — "toda exclusão tem um porquê"). Sem o motivo → 422
// (code: validation); motivo inexistente/inativo → 422 (invalid_cancellation_reason).
// O body do DELETE vai na chave `data` da config do axios.
//
// Handoff 0059: trava UNIVERSAL — item roteirizado (route_subcluster_id != null)
// não é excluível por NENHUM perfil → 422 (code: item_already_routed). O code
// finance-específico (finance_cannot_delete_routed) continua existindo e dispara
// antes para o perfil financeiro. Regra final: só exclui item PENDENTE e fora
// de rota (exclusivamente em NÃO ROTEIRIZADOS).
export async function cancelScheduleItem(itemId, cancellationReasonId) {
   const response = await axios.delete(`/routing/items/${itemId}`, {
      data: { cancellation_reason_id: cancellationReasonId },
   });
   return {
      scheduleItem: response?.data?.data?.scheduleItem ?? null,
      message: response?.data?.message ?? 'Item cancelado.',
   };
}

// Restaura (reverte o cancelamento) um item CANCELADO — handoff 0059.
// Permissão EXCLUSIVA da roteirização: routing:schedule_item:cancel (sem OR com
// routing:finance:operate — assimétrico ao DELETE, de propósito: "a origem vem
// da roteirização, a correção também tem que vir de lá").
//
// Efeito determinístico: item_status volta a PENDENTE; metadados do cancelamento
// (cancelled_at/by/reason) são APAGADOS (sem histórico de reversão); o item sai
// de qualquer rota (route_subcluster_id/route_sequence → null) e volta ao balde
// NÃO ROTEIRIZADOS. UNIQUE-safe por construção (o próprio CANCELADO ocupa a
// chave composta) — não há caso de 409.
//
// Erros: 404 schedule_item_not_found · 422 invalid_state_for_restore (item não
// está CANCELADO) · 403 missing_permission.
export async function restoreScheduleItem(itemId) {
   const response = await axios.post(`/routing/items/${itemId}/restore`);
   return {
      scheduleItem: response?.data?.data?.scheduleItem ?? null,
      message: response?.data?.message ?? 'Item restaurado.',
   };
}

// ===========================================================================
// MOTIVOS DE NÃO REALIZAÇÃO
// ===========================================================================

export async function getNonCompletionReasons() {
   const response = await axios.get('/routing/non-completion-reasons');
   return response?.data?.data?.nonCompletionReasons ?? [];
}

// ===========================================================================
// MOTIVOS DE CANCELAMENTO (EXCLUSÃO) — catálogo (handoff 0048)
// ===========================================================================
// GET /routing/cancellation-reasons?active=1 — catálogo (seed) que alimenta o
// select obrigatório do dialog de exclusão. Permissão: routing:schedule_item:read
// (a mesma do select de não realização). ?active=1 → só ativos (use no select);
// lista ordenada por description. Exibe description, manda id no DELETE.

export async function getCancellationReasons({ active = true } = {}) {
   const response = await axios.get('/routing/cancellation-reasons', {
      params: active ? { active: 1 } : undefined,
   });
   return response?.data?.data?.cancellationReasons ?? [];
}

// ===========================================================================
// COMISSÕES (Painel 4 da Tela 2)
// ===========================================================================

export async function listCommissionEntries(itemId) {
   const response = await axios.get(`/routing/items/${itemId}/commission-entries`);
   return response?.data?.data?.commissionEntries ?? [];
}

export async function createCommissionEntry(itemId, payload) {
   const response = await axios.post(
      `/routing/items/${itemId}/commission-entries`,
      payload,
   );
   return {
      commissionEntry: response?.data?.data?.commissionEntry ?? null,
      message: response?.data?.message ?? 'Lançamento criado.',
   };
}

export async function deleteCommissionEntry(itemId, entryId) {
   const response = await axios.delete(
      `/routing/items/${itemId}/commission-entries/${entryId}`,
   );
   return { message: response?.data?.message ?? 'Lançamento removido.' };
}

// SUGESTÃO DE COMISSÃO (handoff 0039 §4) — GET on-demand, leitura PURA (não
// persiste). Deriva da seleção planejada do item (services[]) × catálogo. Pré-
// preenche o fechamento; o financeiro revisa e confirma criando os lançamentos
// (um POST por linha, via createCommissionEntry — continua exigindo REALIZADO).
//
// Perm: routing:commission_entry:create OR routing:finance:operate.
// Linha (snake_case): { commission_table_id, service_key, service_description,
//   quantity, unit_value, total_value, service_active }. service_active=false
// sinaliza serviço desativado no catálogo (o financeiro decide se lança).
// Item sem seleção → suggestion: [].
export async function getCommissionSuggestion(itemId) {
   const response = await axios.get(
      `/routing/items/${itemId}/commission-suggestion`,
   );
   return response?.data?.data?.suggestion ?? [];
}

export async function listCommissionTable() {
   const response = await axios.get('/routing/commission-table');
   const rows = response?.data?.data?.commissionEntries ?? [];

   // O backend usa a chave "commissionEntries" para a TABELA MESTRE também.
   // Renomeamos para evitar confusão na UI e fazemos snake → camel nas linhas.
   return rows.map((row) => ({
      id:                       row.id,
      serviceCode:              row.service_code,
      serviceDescription:       row.service_description,
      productType:              row.product_type,
      applicableRegion:         row.applicable_region,
      serviceCategory:          row.service_category,
      commissionValuePerUnit:   row.commission_value_per_unit,
   }));
}

// ===========================================================================
// IMPORTAÇÃO REFILTEK — PREVIEW
// ===========================================================================
// POST /routing/import/preview
// Multipart/form-data com:
//   - file              (Blob/File)        arquivo .xlsx do Refiltek
//   - schedule_date     (string YYYY-MM-DD)
//   - schedule_period   ('MANHA' | 'TARDE')
//
// Retorna o conteúdo do envelope.data.preview já desempacotado:
//   {
//     totalRowsInFile,
//     totalValid,
//     totalWarnings,
//     totalErrors,
//     validRows:   [ { ...campos, lineNumber, warnings: [] } ],
//     warningRows: [ { ...campos, lineNumber, warnings: [strings] } ],
//     errorRows:   [ { lineNumber, rawOrderNumber, error } ],
//   }
//
// Os campos das linhas vêm em snake_case (vindos do backend) e são mantidos
// como tal para preservar simetria com o resto do módulo.

export async function previewRefiltekImport({
   file,
   scheduleDate,
   schedulePeriod,
   clusterOrigin,
}) {
   const formData = new FormData();
   formData.append('file', file);
   formData.append('schedule_date', scheduleDate);
   formData.append('schedule_period', schedulePeriod);
   // Ponto 4: base do lote (texto, opcional, default GOIANIA no backend).
   if (clusterOrigin) formData.append('cluster_origin', clusterOrigin);

   const response = await axios.post('/routing/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
   });

   const preview = response?.data?.data?.preview ?? {};

   return {
      totalRowsInFile:  preview.totalRowsInFile  ?? 0,
      totalValid:       preview.totalValid       ?? 0,
      totalWarnings:    preview.totalWarnings    ?? 0,
      totalErrors:      preview.totalErrors      ?? 0,
      validRows:        preview.validRows        ?? [],
      warningRows:      preview.warningRows      ?? [],
      errorRows:        preview.errorRows        ?? [],
      message:          response?.data?.message  ?? null,
   };
}

// ===========================================================================
// IMPORTAÇÃO REFILTEK — CONFIRM
// ===========================================================================
// POST /routing/import/confirm
// Multipart/form-data com:
//   - file                    (Blob/File)
//   - schedule_date           (string YYYY-MM-DD)
//   - schedule_period         ('MANHA' | 'TARDE')
//   - selected_line_numbers   (JSON.stringify do array de inteiros)
//
// IMPORTANTE: selected_line_numbers só pode ser enviado como string em
// multipart, então usamos JSON.stringify. Backend faz JSON.parse no recebimento.
//
// Retorno do backend:
//   {
//     status: 'success',
//     data: {
//       importBatch: {
//         id, batch_uuid, schedule_date, schedule_period, original_filename,
//         total_rows_in_file, total_imported, total_skipped, total_errors
//       }
//     },
//     message: 'Importação concluída: N itens importados'
//   }
//
// O service devolve o importBatch CRU (snake_case) — UI lê os campos diretamente
// (total_imported, total_skipped, total_errors) seguindo o padrão de
// "snake_case nas linhas, camelCase nas margens".

export async function confirmRefiltekImport({
   file,
   scheduleDate,
   schedulePeriod,
   selectedLineNumbers,
   clusterOrigin,
}) {
   const formData = new FormData();
   formData.append('file', file);
   formData.append('schedule_date', scheduleDate);
   formData.append('schedule_period', schedulePeriod);
   // Ponto 4: base do lote (texto, opcional, default GOIANIA no backend).
   if (clusterOrigin) formData.append('cluster_origin', clusterOrigin);
   formData.append('selected_line_numbers', JSON.stringify(selectedLineNumbers ?? []));

   const response = await axios.post('/routing/import/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
   });

   return {
      importBatch: response?.data?.data?.importBatch ?? null,
      message:     response?.data?.message            ?? 'Importação concluída.',
   };
}

// ===========================================================================
// HISTÓRICO DE LOTES (entrega futura)
// ===========================================================================

export async function listImportBatches(filters) {
   const response = await axios.get('/routing/imports', { params: filters });
   const envelope = response?.data ?? {};
   const data = envelope.data ?? {};
   const meta = envelope.meta ?? {};

   return {
      importBatches: data.importBatches ?? [],
      total:         meta.total         ?? 0,
      page:          meta.page          ?? 1,
      perPage:       meta.per_page      ?? 20,
      totalPages:    meta.total_pages   ?? 1,
   };
}

export async function getImportBatchItems(batchId, filters) {
   const response = await axios.get(
      `/routing/imports/${batchId}/items`,
      { params: filters },
   );
   const envelope = response?.data ?? {};
   const data = envelope.data ?? {};
   const meta = envelope.meta ?? {};

   return {
      scheduleItems: data.scheduleItems ?? [],
      total:         meta.total         ?? 0,
      page:          meta.page          ?? 1,
      perPage:       meta.per_page      ?? 20,
      totalPages:    meta.total_pages   ?? 1,
   };
}

// ===========================================================================
// RELATÓRIO DE PRODUTIVIDADE — GET /routing/reports/productivity
// ===========================================================================
// Permissão: routing:report:read
//
// Caller envia filtros em camelCase; o service traduz para snake_case na
// montagem dos query params (convenção do backend pra parâmetros de URL).
//
// A response chega com summary e technicianRankings em camelCase — segue a
// regra "snake_case nas linhas, camelCase nas margens" descrita no header
// deste arquivo. Aqui são agregados/borda do envelope, então não precisam
// de renomeação. Pass-through direto.
//
// Filtros:
//   dateFrom        string YYYY-MM-DD  obrigatório
//   dateTo          string YYYY-MM-DD  obrigatório
//   technicianIds   "id1,id2,..." ou "all"  opcional
//   departmentId    number             opcional
//
// Retorno:
//   { summary, technicianRankings, message }

export async function getProductivityReport({
   dateFrom,
   dateTo,
   technicianIds,
   departmentId,
} = {}) {
   const params = new URLSearchParams();
   if (dateFrom) params.append('date_from', dateFrom);
   if (dateTo)   params.append('date_to',   dateTo);
   if (technicianIds) params.append('technician_ids', technicianIds);
   if (departmentId !== undefined && departmentId !== null && departmentId !== '') {
      params.append('department_id', String(departmentId));
   }

   const response = await axios.get(
      `/routing/reports/productivity?${params.toString()}`,
   );

   const data = response?.data?.data ?? {};

   return {
      summary:             data.summary ?? null,
      dailySeries:         Array.isArray(data.dailySeries)
         ? data.dailySeries
         : [],
      technicianRankings:  Array.isArray(data.technicianRankings)
         ? data.technicianRankings
         : [],
      message:             response?.data?.message ?? null,
   };
}

// ===========================================================================
// INSERÇÃO MANUAL — POST /routing/items
// ===========================================================================
// Cria um RoutingScheduleItem avulso (não vindo de planilha).
// Usado pela aba "Inserção manual" da tela /routing/items/new.
//
// O service injeta automaticamente import_source='MANUAL' no payload, então a
// página NÃO precisa se preocupar com isso.
//
// Whitelist de campos aceitos (espelha a whitelist do PUT, exceto técnico):
//   schedule_date, schedule_period, customer_name, customer_phone,
//   service_address, service_address_complement, order_number, order_value,
//   customer_refiltek_id, department_id, seller_employee_id,
//   is_salesperson_external, payment_collection_type,
//   services (handoff 0039 — [{ commission_table_id, quantity }]), notes.
//
// Ponto 4: aceita cluster_origin (código, opcional). Ausente → GOIANIA (default
// do módulo). O item herda a base do schedule; a resposta traz cluster_origin_id.

export async function createScheduleItem(payload) {
   const sanitizedPayload = {
      ...payload,
      import_source: 'MANUAL',
   };

   const response = await axios.post('/routing/items', sanitizedPayload);

   return {
      scheduleItem: response?.data?.data?.scheduleItem ?? null,
      message:      response?.data?.message ?? 'Item criado.',
   };
}

// ===========================================================================
// REAGENDAMENTO ENCADEADO — POST /routing/items/:itemId/reschedule (M4)
// ===========================================================================
// Permissão: routing:schedule_item:create.
//
// Aplicável a item PENDENTE (fecha em NAO_REALIZADO com o motivo) ou já
// NAO_REALIZADO (só cria a nova tentativa). REALIZADO/CANCELADO → 422.
// Cria uma NOVA linha PENDENTE copiando cliente + serviços + OS em nova
// data/período, com previous_attempt_item_id apontando a anterior.
//
// Body: { schedule_date, schedule_period, non_completion_reason_id, notes? }
// Erros: 409 already_rescheduled (reagendar a mesma origem 2×) ou UNIQUE do
// slot (nova data/período == slot anterior). Resposta: data.scheduleItem = a
// NOVA tentativa (201).

export async function rescheduleScheduleItem(itemId, payload) {
   const response = await axios.post(
      `/routing/items/${itemId}/reschedule`,
      payload,
   );
   return {
      scheduleItem: response?.data?.data?.scheduleItem ?? null,
      message:      response?.data?.message ?? 'Item reagendado.',
   };
}

// ===========================================================================
// CATÁLOGO DE SERVIÇOS — /routing/service-catalog (handoff 0038/0039 — unificado)
// ===========================================================================
// O catálogo agora é a `technician_commission_table` unificada (~35 serviços):
// uma só fonte para LOGÍSTICA (tempo de execução, ciclo de vida) e FINANCEIRO
// (comissão por unidade). Dois donos com permissões distintas:
//   - routing:service_catalog:manage → cria/edita tudo MENOS a comissão.
//   - finance:commission:manage      → edita SÓ commission_value_per_unit.
//
// Leitura: routing:schedule_item:read. Linha (snake_case, convenção do módulo):
//   { id, service_code, service_description, product_type, service_category,
//     estimated_completion_service_time, commission_value_per_unit, is_active,
//     created_at, updated_at }
//
// service_code é IMUTÁVEL (chave de domínio; não vai no PATCH). A criação é só
// da logística — a comissão NASCE 0,00 e o financeiro preenche no endpoint
// dedicado (/:id/commission). O DELETE não apaga: desativa (is_active=0),
// preservando seleções/comissões vinculadas. `estimated_completion_service_time`
// pode vir NULL (logística ainda não parametrizou) → duração calculada 0.

export async function listServiceCatalog({ includeInactive } = {}) {
   const response = await axios.get('/routing/service-catalog', {
      params: includeInactive ? { include_inactive: true } : undefined,
   });
   return response?.data?.data?.serviceCatalog ?? [];
}

// POST — só logística. Body: { service_code, service_description, product_type,
// service_category, estimated_completion_service_time?, is_active? }. 201.
export async function createServiceType(payload) {
   const response = await axios.post('/routing/service-catalog', payload);
   return {
      serviceType: response?.data?.data?.serviceType ?? null,
      message:     response?.data?.message ?? 'Serviço criado.',
   };
}

// PATCH — só logística. Body: qualquer subconjunto de { service_description,
// product_type, service_category, estimated_completion_service_time, is_active }.
// NÃO inclui service_code (imutável) nem commission_value_per_unit (endpoint à parte).
export async function updateServiceType(serviceTypeId, payload) {
   const response = await axios.patch(
      `/routing/service-catalog/${serviceTypeId}`,
      payload,
   );
   return {
      serviceType: response?.data?.data?.serviceType ?? null,
      message:     response?.data?.message ?? 'Serviço atualizado.',
   };
}

// PATCH /:id/commission — só financeiro (finance:commission:manage). Endpoint
// dedicado: separa o dono do valor de comissão do dono do tempo/cadastro.
// Body: { commission_value_per_unit }.
export async function updateServiceTypeCommission(serviceTypeId, commissionValuePerUnit) {
   const response = await axios.patch(
      `/routing/service-catalog/${serviceTypeId}/commission`,
      { commission_value_per_unit: commissionValuePerUnit },
   );
   return {
      serviceType: response?.data?.data?.serviceType ?? null,
      message:     response?.data?.message ?? 'Comissão atualizada.',
   };
}
